import { effectiveRules } from '../alignments/rules';
import { letterCount } from '../level/letters';
import { enclosure } from './planet';
import { derive, nodeMap, type DerivedState } from './state';
import type { Constellation, PlayState, SubmitCode, WordFacts } from './types';

/** The letters of the word in progress. */
export function spelledWord(state: PlayState): string {
  const nodes = nodeMap(state.level);
  let word = '';
  for (const id of state.selection) {
    const node = nodes.get(id);
    if (node && node.type === 'star') word += node.letter;
  }
  return word;
}

/** What the server knows about the word: dictionary membership and, in Puzzle, solution membership. */
export interface WordVerdict {
  inDictionary: boolean;
  /** Puzzle only: the word is one of the level's solution words. */
  isSolution?: boolean;
}

export interface SubmitCheck {
  code: SubmitCode;
  word: string;
  /** For too_short: the level's minimum. */
  minWordLength?: number;
}

/**
 * The submit checks in order: minimum length (as the alignments in force bend
 * it), not already spelled, dictionary (or the solution set in Puzzle). Pure:
 * the caller supplies the verdict.
 */
export function checkSubmission(state: PlayState, verdict: WordVerdict): SubmitCheck {
  const word = spelledWord(state);
  const min = effectiveRules(state).minWordLength;
  if (letterCount(word) < min) return { code: 'too_short', word, minWordLength: min };
  if (state.constellations.some((c) => c.word === word)) return { code: 'already_spelled', word };
  if (state.level.mode === 'puzzle') {
    if (!verdict.isSolution) return { code: verdict.inDictionary ? 'not_in_solution' : 'not_a_word', word };
    return { code: 'accepted', word };
  }
  if (!verdict.inDictionary) return { code: 'not_a_word', word };
  return { code: 'accepted', word };
}

/** Local checks that need no server: length and repetition. */
export function precheckSubmission(state: PlayState): SubmitCheck | null {
  const check = checkSubmission(state, { inDictionary: true, isSolution: true });
  return check.code === 'accepted' ? null : check;
}

/**
 * The word in progress as the constellation it would become: its taps and
 * lines, plus the closing line and the planet when it rings one (planet.ts).
 * The same on the game's preview and the Worker's replay.
 */
export function constellationOf(state: PlayState, derived: DerivedState = derive(state)): Constellation {
  const c: Constellation = {
    word: spelledWord(state),
    nodes: [...state.selection],
    lines: [...state.selectionLines],
  };
  const ring = enclosure(state, derived);
  if (ring) {
    c.lines.push(ring.closing);
    c.planet = { id: ring.planetId, loop: ring.loop };
  }
  return c;
}

/** Turn the word in progress into a constellation. The caller has already checked it, and may pass what the Worker learned about the word. */
export function acceptSelection(state: PlayState, facts?: WordFacts): { state: PlayState; constellation: Constellation } {
  const constellation = constellationOf(state);
  if (facts) constellation.facts = facts;
  return {
    constellation,
    state: { ...state, constellations: [...state.constellations, constellation], selection: [], selectionLines: [] },
  };
}
